import { useEffect, useMemo, useRef, useState } from "react";

import type {
  RowData,
  RowSelectionState,
  Updater,
} from "@tanstack/react-table-v9";

export interface RowSelectionOptions<TData extends RowData> {
  /** Selected row ids, controlled by the caller. Ids on other pages stay selected */
  selectedRowIds?: string[];
  onSelectedRowIdsChange?: (ids: string[]) => void;
  /** Same change with the rows themselves: rows the table has loaded since mount, plus any passed in `selectedRows` */
  onSelectedRowsChange?: (rows: TData[]) => void;
  /** Rows the caller already holds for `selectedRowIds`, so ones the table hasn't loaded, e.g. preselected or on an unvisited page, are still handed back */
  selectedRows?: TData[];
}

const toState = (ids: string[]): RowSelectionState =>
  Object.fromEntries(ids.map((id) => [id, true]));

/** Row selection state and change handler for `useTable`, or nothing when the caller doesn't use these options */
export const useRowSelection = <TData extends RowData>({
  data,
  getRowId,
  getFlatRows,
  selectedRowIds,
  onSelectedRowIdsChange,
  onSelectedRowsChange,
  selectedRows,
}: RowSelectionOptions<TData> & {
  data: TData[];
  getRowId: (row: TData, index: number) => string;
  /** The table's rows, sub-rows included, read after render */
  getFlatRows: () => { id: string; original: TData }[];
}) => {
  const [internal, setInternal] = useState<RowSelectionState>({});
  const idsKey = selectedRowIds?.join("\u0000");
  const controlled = useMemo(
    () => (selectedRowIds ? toState(selectedRowIds) : undefined),
    [idsKey],
  );
  const rowSelection = controlled ?? internal;

  // Updates in the same tick build on each other rather than on the render-time state
  const latest = useRef(rowSelection);
  latest.current = rowSelection;

  // Every row seen on any page, so a selection made on an earlier page can still be handed back
  const seen = useRef(new Map<string, TData>());
  const cacheRows = !!onSelectedRowsChange;
  useEffect(() => {
    if (!cacheRows) return;
    getFlatRows().forEach((row) => seen.current.set(row.id, row.original));
  }, [data, cacheRows]);
  useEffect(() => {
    if (!cacheRows || !selectedRows || !selectedRowIds) return;
    selectedRows.forEach((row, index) => {
      const id = getRowId(row, index);
      if (!seen.current.has(id) && selectedRowIds.includes(id)) {
        seen.current.set(id, row);
      }
    });
  }, [selectedRows, idsKey, cacheRows]);

  if (!selectedRowIds && !onSelectedRowIdsChange && !onSelectedRowsChange) {
    return {};
  }

  const onRowSelectionChange = (updater: Updater<RowSelectionState>) => {
    const next =
      typeof updater === "function" ? updater(latest.current) : updater;
    latest.current = next;
    if (!selectedRowIds) setInternal(next);
    const ids = Object.keys(next).filter((id) => next[id]);
    onSelectedRowIdsChange?.(ids);
    if (!onSelectedRowsChange) return;
    // Loaded rows first, so data that changed since the cache last ran wins
    const loaded = new Map(
      getFlatRows().map((row) => [row.id, row.original] as const),
    );
    onSelectedRowsChange(
      ids
        .map((id) => loaded.get(id) ?? seen.current.get(id))
        .filter((row): row is TData => row !== undefined),
    );
  };

  return { state: { rowSelection }, onRowSelectionChange };
};
